import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function MarketResearchToolsPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Market Research Tools",
        "item": "https://elpasotexasbusiness.com/resources/market-research-tools"
      }
    ]
  };

  const researchCategories = [
    {
      title: 'Demographic Analysis',
      icon: 'ri-group-line',
      description: 'Understand your target customers in El Paso',
      tools: [
        {
          name: 'U.S. Census Bureau',
          description: 'Access detailed demographic data for El Paso County including age, income, education, and household composition.',
          type: 'Free'
        },
        {
          name: 'El Paso Economic Development',
          description: 'Local economic reports, population trends, and business climate data specific to El Paso.',
          type: 'Free'
        },
        {
          name: 'ESRI Business Analyst',
          description: 'Advanced demographic mapping and consumer spending patterns for targeted market analysis.',
          type: 'Paid'
        }
      ]
    },
    {
      title: 'Competitor Research',
      icon: 'ri-search-eye-line',
      description: 'Analyze your competition and find your edge',
      tools: [
        {
          name: 'Google Business Profile',
          description: 'Research local competitors, read customer reviews, and analyze their online presence and ratings.',
          type: 'Free'
        },
        {
          name: 'Yelp Business',
          description: 'Discover competitor strengths and weaknesses through customer feedback and service offerings.',
          type: 'Free'
        },
        {
          name: 'SEMrush',
          description: 'Analyze competitor websites, keywords, advertising strategies, and online market share.',
          type: 'Paid'
        }
      ]
    },
    {
      title: 'Customer Surveys',
      icon: 'ri-questionnaire-line',
      description: 'Gather direct feedback from potential customers',
      tools: [
        {
          name: 'Google Forms',
          description: 'Create free surveys to collect customer preferences, needs, and feedback from your target market.',
          type: 'Free'
        },
        {
          name: 'SurveyMonkey',
          description: 'Professional survey platform with advanced analytics and customizable templates for market research.',
          type: 'Free/Paid'
        },
        {
          name: 'Typeform',
          description: 'Engaging, conversational surveys that increase response rates and provide valuable insights.',
          type: 'Free/Paid'
        }
      ]
    },
    {
      title: 'Industry Research',
      icon: 'ri-bar-chart-grouped-line',
      description: 'Stay informed about industry trends and opportunities',
      tools: [
        {
          name: 'IBISWorld',
          description: 'Comprehensive industry reports with market size, trends, and forecasts for thousands of industries.',
          type: 'Paid'
        },
        {
          name: 'Statista',
          description: 'Access statistics, market data, and industry insights across various sectors and regions.',
          type: 'Free/Paid'
        },
        {
          name: 'Trade Associations',
          description: 'Join industry-specific associations for insider knowledge, networking, and market intelligence.',
          type: 'Varies'
        }
      ]
    },
    {
      title: 'Local Market Data',
      icon: 'ri-map-pin-2-line',
      description: 'El Paso-specific market intelligence',
      tools: [
        {
          name: 'El Paso Chamber of Commerce',
          description: 'Local business reports, economic indicators, and networking opportunities with established businesses.',
          type: 'Free/Member'
        },
        {
          name: 'City of El Paso Open Data',
          description: 'Access public datasets on permits, licenses, zoning, and business registrations in El Paso.',
          type: 'Free'
        },
        {
          name: 'Texas Workforce Commission',
          description: 'Labor market information, wage data, and employment trends for the El Paso region.',
          type: 'Free'
        }
      ]
    },
    {
      title: 'Social Media Insights',
      icon: 'ri-chat-poll-line',
      description: 'Understand customer behavior and preferences online',
      tools: [
        {
          name: 'Facebook Audience Insights',
          description: 'Analyze demographics, interests, and behaviors of your target audience on Facebook.',
          type: 'Free'
        },
        {
          name: 'Instagram Insights',
          description: 'Track engagement, reach, and audience demographics for your Instagram business profile.',
          type: 'Free'
        },
        {
          name: 'Hootsuite Analytics',
          description: 'Monitor social media trends, competitor activity, and customer sentiment across platforms.',
          type: 'Paid'
        }
      ]
    }
  ];

  const researchSteps = [
    {
      number: '01',
      title: 'Define Your Research Goals',
      description: 'Clearly identify what you need to learn about your market, customers, and competition.'
    },
    {
      number: '02',
      title: 'Identify Your Target Market',
      description: 'Determine who your ideal customers are in El Paso based on demographics and behaviors.'
    },
    {
      number: '03',
      title: 'Gather Primary Data',
      description: 'Conduct surveys, interviews, and focus groups to collect firsthand information.'
    },
    {
      number: '04',
      title: 'Collect Secondary Data',
      description: 'Research existing reports, studies, and data from reliable sources and databases.'
    },
    {
      number: '05',
      title: 'Analyze Your Findings',
      description: 'Look for patterns, trends, and insights that will inform your business strategy.'
    },
    {
      number: '06',
      title: 'Apply Your Insights',
      description: 'Use your research to make informed decisions about products, pricing, and marketing.'
    }
  ];

  const relatedResources = [
    {
      title: 'Financial Projections Guide',
      description: 'Use market research data to create accurate financial forecasts and validate your business model.',
      path: '/resources/financial-projections-guide',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'Business Plan Template',
      description: 'Incorporate your market research findings into a comprehensive business plan.',
      path: '/resources/business-plan-template',
      icon: 'ri-file-text-line'
    },
    {
      title: 'El Paso Demographics',
      description: 'Explore detailed demographic insights about El Paso\'s bilingual market and consumer base.',
      path: '/demographics',
      icon: 'ri-group-line'
    }
  ];

  return (
    <>
      <SEOHead
        title="Market Research Tools for El Paso Businesses"
        description="Essential market research tools and resources for El Paso entrepreneurs. Access demographic data, competitor analysis, consumer insights, and border market trends to validate your business idea."
        canonical="https://elpasotexasbusiness.com/resources/market-research-tools"
        ogImage="https://public.readdy.ai/ai/img_res/8d6244c9904fff639992230f5ccf2290.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main id="main-content" className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200 pt-24">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <Link 
                to="/resources" 
                className="inline-flex items-center space-x-2 text-[#D2691E] hover:text-[#B8571A] transition-colors cursor-pointer"
                aria-label="Return to Resources page"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="font-semibold text-sm">Back to Resources</span>
              </Link>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="mb-16">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-[#D2691E] rounded-2xl flex items-center justify-center">
                  <i className="ri-search-line text-4xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-gray-900">Market Research Tools</h1>
                  <p className="text-lg text-gray-600 mt-2">Essential resources for understanding your El Paso target market</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-10 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">The Market Research Process</h2>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Effective market research helps you understand your customers, validate your business idea, and make data-driven decisions. Follow these steps to conduct thorough market research for your El Paso business.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {researchSteps.map((step, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6">
                    <div className="text-5xl font-bold text-[#D2691E]/20 mb-4">{step.number}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Research Tools & Resources</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Leverage these powerful tools to gather comprehensive market intelligence and gain a competitive advantage in El Paso.
              </p>
              
              <div className="space-y-12">
                {researchCategories.map((category, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center">
                        <i className={`${category.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{category.title}</h3>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {category.tools.map((tool, toolIndex) => (
                        <div key={toolIndex} className="bg-[#FAFAF8] rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="text-lg font-bold text-gray-900">{tool.name}</h4>
                            <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${
                              tool.type === 'Free' ? 'bg-green-100 text-green-700' :
                              tool.type === 'Paid' ? 'bg-[#D2691E]/10 text-[#D2691E]' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {tool.type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">{tool.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-10 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Pro Tips for Effective Market Research</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Start with Free Resources</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Begin with free tools and government data sources before investing in paid research platforms.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Talk to Real Customers</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nothing beats direct conversations with potential customers in El Paso to understand their needs.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-refresh-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Keep Research Ongoing</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Market conditions change. Continuously monitor trends and update your research regularly.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-focus-3-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Focus on Actionable Insights</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Collect data that will directly inform your business decisions and strategy.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12 text-center mb-16">
              <h3 className="text-4xl font-bold text-white mb-4">Ready to Research Your Market?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Start using these tools today to gain valuable insights about your El Paso target market and competition.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/resources/business-plan-template"
                  className="inline-flex items-center space-x-3 bg-white text-[#1B3A3A] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="View Business Plan Template"
                >
                  <span>Business Plan Template</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="Contact us for expert assistance"
                >
                  <span>Get Expert Help</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}